import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { Flex } from '@ui-library/misc';
import Button from '@ui-library/button';
import AceEditor from 'react-ace';
import { Completer } from 'brace';
import 'brace/mode/sql';
import 'brace/theme/textmate';
import 'brace/ext/language_tools';
import { WithQueryBuilderProps, withQueryBuilderState } from './queryBuilderContext';
import { isEmpty } from 'lodash';
import { FixMe } from '@utils/types';
import { FieldType } from './types';
import classes from './style.module.scss';
import { getAutosuggestion, preventStatementCommandDeletion } from './utils';

interface QueryEditorProps {
  onChange: (query: string) => void;
  disabled?: boolean;
}
interface QueryEditorState {
  editMode: boolean;
  inputValue?: string;
  prevResource?: string;
}

class QueryEditor extends React.PureComponent<
  QueryEditorProps & WithQueryBuilderProps,
  QueryEditorState
> {
  // autoCompleter adds autocompletion to the editor about available fields and resources
  autoCompleter: Completer = {
    // add a description dialog to the completion field
    getDocTooltip: (item: FixMe) => {
      if (item.description || !isEmpty(item.parameters)) {
        item.docHTML = ReactDOMServer.renderToStaticMarkup(
          <div className="function-docs" style={{ maxWidth: 400, whiteSpace: 'normal' }}>
            <p>{item.description}</p>
          </div>,
        );
      }
    },
    // eslint-disable-next-line max-params
    getCompletions: (editor, session, cursorPosition, prefix, callback) => {
      const currentValue = editor.getValue().toLowerCase();
      const cursorIndex = editor.session.doc.positionToIndex(cursorPosition, 0) - 1;
      const autoCompleteValues: FieldType[] = getAutosuggestion(
        currentValue,
        cursorIndex,
        this.props.queryBuilderState.resources,
        this.props.queryBuilderState.fields,
      );

      callback(
        null,
        autoCompleteValues.map((field) => ({
          value: field.value!,
          meta: field.category,
          description: field.description,
        })),
      );
    },
  };

  static getDerivedStateFromProps(
    props: QueryEditorProps & WithQueryBuilderProps,
    state: QueryEditorState,
  ) {
    const { prevResource } = state;
    const { editMode, query, resource, inputQuery } = props.queryBuilderState;
    // let inputValue = inputQuery;
    let inputValue = editMode ? inputQuery : query;
    if (prevResource !== resource) {
      inputValue = inputValue?.replace(` ${prevResource} `, ` ${resource} `);
    }
    return {
      ...state,
      prevResource: resource,
      inputValue,
    };
  }

  constructor(props: QueryEditorProps & WithQueryBuilderProps) {
    super(props);
    this.state = {
      editMode: false,
      inputValue: this.props.queryBuilderState.query,
    };
  }

  componentDidMount() {
    this.editor?.commands.on('exec', preventStatementCommandDeletion);
  }

  editor: FixMe;

  isDisabled = () => {
    const { disabled, queryBuilderState } = this.props;
    const { loading, resource } = queryBuilderState;
    return disabled || loading || !resource;
  };

  toggleEditMode = () => {
    if (this.isDisabled()) {
      return;
    }
    const { editMode, toggleEditMode } = this.props.queryBuilderState;
    this.showCursor(!editMode);
    toggleEditMode();
  };

  reset = () => {
    if (this.isDisabled()) {
      return;
    }
    const { reset } = this.props.queryBuilderState;
    reset();
  };

  showCursor = (show: boolean) => {
    if (!this.editor) {
      return;
    }
    this.editor.renderer.$cursorLayer.element.style.display = show ? '' : 'none';
  };

  render() {
    const { onChange, queryBuilderState } = this.props;
    const { editMode } = queryBuilderState;
    const readOnlyEditor = !editMode || this.isDisabled();
    return (
      <Flex flexDirection="column" className={classes.editorWrapper}>
        <div className={classes.editor} data-testid="ace-editor">
          <AceEditor
            mode="sql"
            width="100%"
            fontSize={16}
            showPrintMargin={false}
            value={this.state.inputValue}
            onChange={onChange}
            highlightActiveLine={false}
            showGutter={false}
            readOnly={readOnlyEditor}
            wrapEnabled
            maxLines={5}
            onLoad={(editor) => {
              this.editor = editor;
              this.showCursor(false);
            }}
            setOptions={{
              // The Ace component supports completer but is not defined in the declarations
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              enableBasicAutocompletion: [this.autoCompleter],
              enableLiveAutocompletion: true,
              newLineMode: false,
            }}
          />
        </div>
        <Flex className={classes.editorButtonsWrapper} justifyContent="flex-end">
          <Button textPrimary onClick={this.reset}>
            Reset
          </Button>
          <Button textPrimary onClick={this.toggleEditMode}>
            {editMode ? 'Cancel edit' : 'Edit Query'}
          </Button>
        </Flex>
      </Flex>
    );
  }
}

export default withQueryBuilderState<QueryEditorProps>(QueryEditor);
